import React, { useLayoutEffect } from 'react';
import { useConfirmDialog } from '../../../../../components/ConfirmDialog';
import {
  Block,
  QaCertificationRow,
  QaListBlockData,
  RowFragment,
  blankQaRow,
  qaRowIsUnedited,
} from '../../../config';
import { setCaretOffset } from '../../../utils/caretRestore';
import LiveText from '../../LiveText';
import './qa-list-block.scss';

/**
 * A detail line Enter has just asked for, waiting for the render that mounts
 * it. Module-level rather than per instance: when the list is split across
 * pages, the row can be drawn by a different QaListBlock from the one that
 * handled the key.
 */
let pendingDetailFocus: string | null = null;

/**
 * One of a row's two editable lines. Only the page's copy is editable -- the
 * measurement copy renders with isEditing false -- so this finds the one the
 * author is looking at.
 */
const findField = (rowId: string, field: 'text' | 'detail'): HTMLElement | null =>
  document.querySelector<HTMLElement>(
    `[data-qa-row="${rowId}"] .qa-row__${field}[contenteditable="true"]`
  );

const focusAtEnd = (el: HTMLElement) => {
  el.focus();
  setCaretOffset(el, (el.textContent || '').length);
};

interface QaListBlockProps {
  block: Block;
  onChange: (data: QaListBlockData) => void;
  isEditing: boolean;
  /**
   * When the list spans more than one page, the items this page shows. Absent
   * means the whole list (the measurement copy, and any list that fits).
   */
  fragment?: RowFragment;
}

/**
 * The QA results an As Constructed Certificate relies on: one bullet per QA
 * item the RWC PEP has ticked, each with the laboratory's reference under it.
 *
 * The rows are seeded from the job's ticked QA items and AUTHORED from there
 * on -- see QaCertificationRow. Refresh (in the Fields sidebar) reconciles
 * membership without touching what has been written.
 *
 * Both lines of a row are rendered even when empty, and DocumentPDF renders
 * them too: the editor measures this block to decide where the page breaks,
 * and anything one surface shows and the other hides makes that measurement a
 * lie. Their min-heights exist for the same reason -- an empty line that
 * collapses on one surface and not the other moves everything below it. The
 * one way a line goes is the author removing the detail line outright
 * (`detail` null), which both surfaces then omit together.
 */
const QaListBlock: React.FC<QaListBlockProps> = ({
  block,
  onChange,
  isEditing,
  fragment,
}) => {
  const { confirm, dialog: confirmDialog } = useConfirmDialog();

  const data = block.data as QaListBlockData;
  const allRows = data.rows ?? [];

  // Only this page's slice is rendered, but every edit addresses the WHOLE
  // list -- otherwise removing a bullet on page 2 would remove the wrong one.
  const rows = fragment ? allRows.slice(fragment.rowStart, fragment.rowEnd) : allRows;

  const setRows = (next: QaCertificationRow[]) => onChange({ ...data, rows: next });

  const updateRow = (id: string, patch: Partial<QaCertificationRow>) =>
    setRows(allRows.map(r => (r.id === id ? { ...r, ...patch } : r)));

  /**
   * An untouched bullet just goes -- a refresh puts it straight back if its QA
   * item is still completed. One that has been written into is asked about: the
   * refresh reseeds the placeholder wording rather than restoring what was
   * typed, so the sentence and the laboratory's reference are gone for good.
   */
  const removeRow = async (row: QaCertificationRow) => {
    if (!qaRowIsUnedited(row)) {
      const proceed = await confirm({
        title: 'Remove this QA item?',
        message: (
          <>
            <p>What you have written into it will be removed from the certificate:</p>
            <ul>
              <li>{row.text}</li>
              {row.detail && <li>{row.detail}</li>}
            </ul>
            <p>Refreshing brings the bullet back, but not the wording.</p>
          </>
        ),
        confirmLabel: 'Remove',
        tone: 'danger',
      });
      if (!proceed) return;
    }
    setRows(allRows.filter(r => r.id !== row.id));
  };

  // A bullet with no qa_type: the PEP has no counterpart for it, so no refresh
  // ever drops it and no refresh ever restores it. Blank, so it reads as a line
  // waiting to be typed into rather than as somebody else's half-written
  // sentence.
  const addRowAfter = (id: string) => {
    const index = allRows.findIndex(r => r.id === id);
    const next = [...allRows];
    next.splice(index + 1, 0, blankQaRow());
    setRows(next);
  };

  /**
   * Not every QA result has a reference to give, so Backspace in an EMPTY
   * detail line takes the line and its dash away, and the caret goes to the end
   * of the bullet above -- as Backspace at the start of an empty line does in
   * Word.
   *
   * Focus moves BEFORE the row changes, on purpose. The detail field may hold
   * a commit still waiting on its timer, and LiveText flushes that on unmount
   * from the previous render's closure -- which would write `detail: ''` back
   * over the null and restore the line. Moving focus blurs it first, and the
   * blur commits synchronously, so the removal below is the last write.
   */
  const handleDetailKeyDown = (row: QaCertificationRow) => (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Backspace' || (e.currentTarget.textContent || '') !== '') return;
    e.preventDefault();
    const text = findField(row.id, 'text');
    if (text) focusAtEnd(text);
    updateRow(row.id, { detail: null });
  };

  /**
   * Enter in the bullet goes to its detail line, bringing the line back first
   * if it was removed -- the way back from the Backspace above. It never
   * inserts a line break: the bullet is one paragraph, and `textContent` would
   * drop the break anyway.
   */
  const handleTextKeyDown = (row: QaCertificationRow) => (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (row.detail === null) {
      pendingDetailFocus = row.id;
      updateRow(row.id, { detail: '' });
      return;
    }
    const detail = findField(row.id, 'detail');
    if (detail) focusAtEnd(detail);
  };

  // The restored line exists only after the render that follows Enter. Child
  // layout effects run first, so its LiveText has already written its text.
  useLayoutEffect(() => {
    if (!pendingDetailFocus) return;
    const el = findField(pendingDetailFocus, 'detail');
    if (!el) return;
    pendingDetailFocus = null;
    focusAtEnd(el);
  });

  return (
    <div className="qa-list-block">
      {confirmDialog}
      {allRows.length === 0 ? (
        // The same words DocumentPDF draws. What to do about it belongs in the
        // Fields sidebar beside the Refresh button, not in a sentence one
        // surface measures and the other does not.
        <p className="qa-list-block__empty">No QA items listed.</p>
      ) : (
        <div className="qa-list-block__rows">
          {rows.map(row => (
            <div key={row.id} className="qa-row" data-qa-row={row.id}>
              <div className="qa-row__line">
                {/* The bullet list's marker and column, so these line up with
                    any bullet list in the document. */}
                <span className="qa-row__bullet" contentEditable={false}>•</span>
                <LiveText
                  className="qa-row__text"
                  value={row.text}
                  editable={isEditing}
                  restoreKey={`${block.id}:${row.id}:text`}
                  onCommit={text => updateRow(row.id, { text })}
                  onKeyDown={handleTextKeyDown(row)}
                />
              </div>
              {/* Null when the author removed it; DocumentPDF drops it too. */}
              {row.detail !== null && (
                <div className="qa-row__line">
                  <span className="qa-row__indent" contentEditable={false} />
                  <span className="qa-row__dash" contentEditable={false}>-</span>
                  <LiveText
                    className="qa-row__detail"
                    value={row.detail}
                    editable={isEditing}
                    restoreKey={`${block.id}:${row.id}:detail`}
                    onCommit={detail => updateRow(row.id, { detail })}
                    onKeyDown={handleDetailKeyDown(row)}
                  />
                </div>
              )}

              {isEditing && (
                <div className="qa-row__actions" contentEditable={false}>
                  <button
                    type="button"
                    title="Add a bullet below"
                    aria-label="Add a bullet below"
                    onClick={() => addRowAfter(row.id)}
                  >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                      <path d="M8 3v10M3 8h10" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    title="Remove this bullet"
                    aria-label="Remove this bullet"
                    onClick={() => { void removeRow(row); }}
                  >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4l8 8M12 4l-8 8" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default QaListBlock;
